import React, { useState, useMemo, useCallback } from 'react';
import { Modal, Button, Form, Alert, Badge, Spinner } from 'react-bootstrap';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import {
  getInvoicePaymentsPaymentId,
  getInvoicePaymentsPaymentIdBankTransactionCandidates,
  postBankTransactionsTransactionIdSync,
  getInvoicePaymentsPaymentIdAttachments,
  getInvoicePaymentsPaymentIdAttachmentsAttachmentId,
  getBankTransactionsTransactionId,
  postBankTransactionPayments,
  patchInvoicePaymentsPaymentId,
  deleteInvoicePaymentsPaymentId,
  deleteBankTransactionPaymentsId,
  useGetUsersId,
} from '../api/generated/arrayscapeGamingDashboardAPI';
import type {
  Payment,
  PaymentAttachment,
  PaymentMethod,
  PaymentLinkStatus,
  BankTransaction,
  LinkedBankTransaction,
} from '../api/generated/model';
import { RootState } from '../store';
import BankTransactionsTable from './BankTransactionsTable';
import InvoiceDetailModal from './InvoiceDetailModal';
import TransactionDetailModal from './TransactionDetailModal';
import { qboPaymentUrl } from './InvoiceDetailContent';
import AttachmentViewer from './common/AttachmentViewer';
import { CopyButton } from './common/Copyable';

const LINKING_ROLES = ['admin', 'finance', 'vp_sales'];

interface PaymentDetailModalProps {
  paymentId: string;          // composite "<src>:<id>"
  onClose: () => void;
  onChanged?: () => void;     // fired after a save / mark / void / delete that mutated state
}

const formatCurrency = (s?: string | number): string => {
  if (s === undefined || s === null || s === '') return '';
  const n = typeof s === 'number' ? s : parseFloat(s);
  if (Number.isNaN(n)) return String(s);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
};

const formatDate = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
};

const methodLabel = (m?: PaymentMethod): string => {
  switch (m) {
    case 'cash': return 'Cash';
    case 'check': return 'Check';
    case 'ach': return 'ACH';
    case 'credit_card': return 'Credit Card';
    case 'debit_card': return 'Debit Card';
    case 'wire': return 'Wire';
    case 'other': return 'Other';
    default: return '';
  }
};

const linkStatusBadge = (s?: PaymentLinkStatus) => {
  switch (s) {
    case 'linked': return <Badge bg="success">Linked</Badge>;
    case 'unlinked': return <Badge bg="warning" text="dark">Unlinked</Badge>;
    case 'unlinkable': return <Badge bg="secondary">Unlinkable</Badge>;
    case 'stranded': return <Badge bg="danger">Stranded</Badge>;
    default: return null;
  }
};

const PaymentDetailModal: React.FC<PaymentDetailModalProps> = ({ paymentId, onClose, onChanged }) => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { data: userData } = useGetUsersId(user?.id || '', { query: { enabled: !!user?.id } });
  const queryClient = useQueryClient();
  const canLink = !!user?.role && LINKING_ROLES.includes(user.role);

  // Always re-fetch the payment when opening the modal so derived link_status,
  // linked_bank_transactions, etc. are fresh after sync.
  const { data: payment, isLoading: paymentLoading, refetch } = useQuery({
    queryKey: ['payment', paymentId],
    queryFn: () => getInvoicePaymentsPaymentId(paymentId),
    enabled: !!paymentId,
  });

  const linkStatus = payment?.link_status as PaymentLinkStatus | undefined;
  const isUnlinked = linkStatus === 'unlinked';
  const isStranded = linkStatus === 'stranded';
  const isUnlinkable = linkStatus === 'unlinkable';
  const isLinked = linkStatus === 'linked';

  // Candidates fetch — only when modal can show the picker (unlinked + role).
  const { data: candidatesData } = useQuery({
    queryKey: ['payment-bt-candidates', paymentId],
    queryFn: () => getInvoicePaymentsPaymentIdBankTransactionCandidates(paymentId),
    enabled: !!payment && isUnlinked && canLink,
  });
  const candidates: BankTransaction[] = (candidatesData as any)?.data || [];

  // Per-row "Revalidate" handler for the candidate BTs table — POSTs
  // /bank-transactions/{transactionId}/sync (path id is the BT UUID
  // PK). The backend refetches from upstream and runs the auto-linker;
  // on success the candidate may have been linked and disappear from
  // the picker. Invalidate the candidates query AND the parent payment
  // query so the modal's link-state derivation reflects any new BTP.
  const handleRevalidateBT = useCallback(async (bt: BankTransaction) => {
    const id = bt.id || '';
    if (!id) return;
    try {
      await postBankTransactionsTransactionIdSync(id);
    } finally {
      await queryClient.invalidateQueries({ queryKey: ['payment-bt-candidates', paymentId] });
      await refetch();
    }
  }, [queryClient, paymentId, refetch]);

  const [selectedBtIds, setSelectedBtIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [invoiceModalId, setInvoiceModalId] = useState<string | null>(null);
  const [selectedBankTxn, setSelectedBankTxn] = useState<BankTransaction | null>(null);

  const openBankTxn = async (btId: string) => {
    try {
      const bt = await getBankTransactionsTransactionId(btId);
      setSelectedBankTxn(bt as BankTransaction);
    } catch (err: any) {
      window.alert(`Failed to load bank transaction ${btId}: ${err?.response?.data?.message || err?.message || err}`);
    }
  };

  // Mark Unlinkable sub-flow.
  const [markUnlinkable, setMarkUnlinkable] = useState(false);
  const [unlinkableReason, setUnlinkableReason] = useState('');

  const apps = useMemo(() => payment?.applied_invoices || [], [payment]);
  const linkedBTs: LinkedBankTransaction[] = useMemo(
    () => payment?.linked_bank_transactions || [],
    [payment]
  );
  const exposureCount = payment?.commission_exposure_count || 0;

  const splitId = (id: string): { srcId: string; pid: string } => {
    const ix = id.indexOf(':');
    return { srcId: ix >= 0 ? id.slice(0, ix) : '', pid: ix >= 0 ? id.slice(ix + 1) : id };
  };

  const refresh = async () => {
    queryClient.invalidateQueries({ queryKey: ['payment', paymentId] });
    queryClient.invalidateQueries({ queryKey: ['payment-bt-candidates', paymentId] });
    queryClient.invalidateQueries({ queryKey: ['invoice-payments'] });
    // Awaited so callers (e.g. unlink) don't release `busy` before the
    // updated data is in — without this the modal renders with stale
    // payment.linked_bank_transactions for one frame after a mutation.
    await refetch();
    onChanged?.();
  };

  const saveLink = async () => {
    if (!payment || selectedBtIds.length === 0) {
      setErr('Select at least one bank transaction to link.');
      return;
    }
    const { srcId, pid } = splitId(payment.id || '');
    setBusy(true); setErr(null);
    try {
      for (const btId of selectedBtIds) {
        await postBankTransactionPayments({
          transaction_id: btId,
          payment_src_id: srcId,
          payment_id: pid,
        });
      }
      setSelectedBtIds([]);
      await refresh();
    } catch (e: any) {
      setErr(e?.response?.data?.message || `Failed to link: ${e}`);
    } finally {
      setBusy(false);
    }
  };

  const submitMarkUnlinkable = async () => {
    const reason = unlinkableReason.trim();
    if (!reason) { setErr('A reason is required to mark this payment unlinkable.'); return; }
    setBusy(true); setErr(null);
    try {
      await patchInvoicePaymentsPaymentId(paymentId, [
        { op: 'replace' as any, path: '/unlinkable_reason', value: reason },
        { op: 'replace' as any, path: '/unlinkable', value: true },
      ]);
      setMarkUnlinkable(false);
      setUnlinkableReason('');
      await refresh();
    } catch (e: any) {
      setErr(e?.response?.data?.message || `Failed to mark unlinkable: ${e}`);
    } finally { setBusy(false); }
  };

  const markLinkable = async () => {
    setBusy(true); setErr(null);
    try {
      await patchInvoicePaymentsPaymentId(paymentId, [
        { op: 'replace' as any, path: '/unlinkable', value: false },
      ]);
      await refresh();
    } catch (e: any) {
      setErr(e?.response?.data?.message || `Failed: ${e}`);
    } finally { setBusy(false); }
  };

  const voidPayment = async () => {
    if (!window.confirm(`Void this payment in its source system? This is reversible (preserves audit trail).`)) return;
    setBusy(true); setErr(null);
    try {
      await patchInvoicePaymentsPaymentId(paymentId, [
        { op: 'replace' as any, path: '/status', value: 'voided' },
      ]);
      await refresh();
    } catch (e: any) {
      setErr(e?.response?.data?.message || `Failed to void: ${e}`);
    } finally { setBusy(false); }
  };

  const deletePayment = async () => {
    if (!window.confirm(`Delete this payment from its source system? This is NOT reversible.`)) return;
    setBusy(true); setErr(null);
    try {
      await deleteInvoicePaymentsPaymentId(paymentId);
      await refresh();
      onClose();
    } catch (e: any) {
      setErr(e?.response?.data?.message || `Failed to delete: ${e}`);
    } finally { setBusy(false); }
  };

  const unlink = async (btpId: string) => {
    if (!window.confirm('Unlink this manual link? Neither the payment nor the bank transaction is touched — only the link between them is removed.')) return;
    setBusy(true); setErr(null);
    try {
      await deleteBankTransactionPaymentsId(btpId);
      await refresh();
    } catch (e: any) {
      setErr(e?.response?.data?.message || `Failed to unlink: ${e}`);
    } finally { setBusy(false); }
  };

  if (paymentLoading || !payment) {
    return (
      <Modal show onHide={onClose} size="xl">
        <Modal.Body className="text-center py-5">
          <Spinner animation="border" className="me-2" /> Loading payment...
        </Modal.Body>
      </Modal>
    );
  }

  const showLinkPicker = isUnlinked && canLink;

  return (
    <>
      <Modal show={!selectedBankTxn && !invoiceModalId} onHide={onClose} size="xl" dialogClassName="payment-detail-modal modal-95w" scrollable fullscreen="lg-down">
      {/* Force modal-content to a definite height so the inner
          DataTable's percentage-height scroll mechanism resolves. */}
      <style>{`.payment-detail-modal .modal-content { height: calc(100vh - 3.5rem); }`}</style>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-3 flex-wrap w-100">
            <span>Payment</span>
            <code className="fs-6 text-muted">{paymentId}</code>
            {linkStatusBadge(linkStatus)}
            {payment.status === 'voided' && <Badge bg="dark">Voided</Badge>}
            {(() => {
              const url = qboPaymentUrl(payment);
              return url ? (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-secondary btn-sm ms-auto me-3"
                >
                  <i className="bi bi-box-arrow-up-right me-1"></i>
                  QuickBooks
                </a>
              ) : null;
            })()}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-3 d-flex flex-column gap-3" style={{ minHeight: 0 }}>
          {err && <Alert variant="danger" onClose={() => setErr(null)} dismissible>{err}</Alert>}

          {/* Payment summary — one cell per attribute. */}
          <div className="border rounded p-3">
            <h6 className="mb-3">Details</h6>
            <dl className="row mb-0">
              <dt className="col-sm-3">Date</dt>
              <dd className="col-sm-9">{formatDate(payment.txn_date)}</dd>

              <dt className="col-sm-3">Amount</dt>
              <dd className="col-sm-9"><strong>{formatCurrency(payment.total_amount)}</strong></dd>

              <dt className="col-sm-3">Customer</dt>
              <dd className="col-sm-9">{payment.customer_name || '—'}</dd>

              <dt className="col-sm-3">Method</dt>
              <dd className="col-sm-9">{methodLabel(payment.method) || '—'}</dd>

              <dt className="col-sm-3">Reference</dt>
              <dd className="col-sm-9">{payment.reference || '—'}</dd>

              <dt className="col-sm-3">Note</dt>
              <dd className="col-sm-9">{payment.note || '—'}</dd>

              <dt className="col-sm-3">Deposit Account</dt>
              <dd className="col-sm-9">{payment.deposit_to_account_id || '—'}</dd>

              <dt className="col-sm-3">Currency</dt>
              <dd className="col-sm-9">
                {payment.currency_code || 'USD'}
                {payment.exchange_rate && payment.exchange_rate !== '1' && ` (rate ${payment.exchange_rate})`}
              </dd>

              <dt className="col-sm-3">Applied Invoices</dt>
              <dd className="col-sm-9">
                {apps.length === 0 ? <span className="text-muted">—</span> : (
                  <span className="d-inline-flex flex-wrap align-items-baseline gap-2">
                    {apps.map((a, i) => {
                      const compositeId = a.invoice_src_id && a.invoice_id
                        ? `${a.invoice_src_id}-${a.invoice_id}`
                        : null;
                      const label = a.invoice_number || a.invoice_id || '';
                      return (
                        <span key={`${a.invoice_src_id}:${a.invoice_id}:${i}`} className="d-inline-flex align-items-baseline gap-1">
                          <Button
                            variant="link"
                            className="p-0 text-decoration-none align-baseline"
                            onClick={(e) => { e.stopPropagation(); compositeId && setInvoiceModalId(compositeId); }}
                            title={a.amount ? `${label} — ${formatCurrency(a.amount)}` : label}
                            disabled={!compositeId}
                          >
                            {label}
                          </Button>
                          <CopyButton
                            value={label}
                            variant="link"
                            size="sm"
                            className="p-0 text-muted align-baseline"
                            title={`Copy ${label}`}
                          />
                        </span>
                      );
                    })}
                  </span>
                )}
              </dd>
            </dl>
          </div>

          {/* Attachments (synced from QB). */}
          {payment.id && <PaymentAttachments paymentId={payment.id} />}

          {/* Linked bank transactions (Linked or Stranded). */}
          {(isLinked || isStranded) && linkedBTs.length > 0 && (
            <div className="border rounded p-3">
              <h6 className="mb-3">Linked Bank Transaction{linkedBTs.length === 1 ? '' : 's'}</h6>
              <table className="table table-sm mb-0">
                <thead>
                  <tr className="text-muted">
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Counterparty</th>
                    <th>Bank Transaction</th>
                    <th>Status</th>
                    {canLink && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {linkedBTs.map(lbt => {
                    const canOpen = !!lbt.bank_transaction_id && !lbt.deleted;
                    const noteShort = lbt.note && lbt.note.length > 40 ? lbt.note.substring(0, 40) + '…' : lbt.note;
                    return (
                      <tr
                        key={lbt.btp_id || ''}
                        style={{ cursor: canOpen ? 'pointer' : 'default' }}
                        onClick={() => canOpen && openBankTxn(lbt.bank_transaction_id!)}
                        title={canOpen ? 'Open bank transaction (edit remittance, view check image, etc.)' : undefined}
                      >
                        <td>{lbt.date ? formatDate(typeof lbt.date === 'string' ? lbt.date : new Date(lbt.date as any).toISOString()) : '—'}</td>
                        <td>{formatCurrency(lbt.amount)}</td>
                        <td>{lbt.counterparty_name || '—'}</td>
                        <td>
                          <div className="d-flex flex-wrap align-items-center gap-1">
                            {lbt.kind && <Badge bg="secondary" className="text-uppercase">{lbt.kind}</Badge>}
                            {lbt.method && <span className="small text-muted text-uppercase">{lbt.method}</span>}
                            {lbt.account_name && <span className="small text-muted">· {lbt.account_name}</span>}
                            {lbt.trace_number && <span className="small text-muted">· trace <code>{lbt.trace_number}</code></span>}
                            {lbt.sender_trace && <span className="small text-muted">· sender <code>{lbt.sender_trace}</code></span>}
                            {noteShort && <span className="small text-muted fst-italic" title={lbt.note}>· {noteShort}</span>}
                          </div>
                        </td>
                        <td>
                          {lbt.deleted
                            ? <Badge bg="danger">deleted</Badge>
                            : (lbt.matched_by === 'auto'
                                ? <Badge bg="info">auto-linked</Badge>
                                : <Badge bg="success">manually linked</Badge>)}
                        </td>
                        {canLink && (
                          <td className="text-end" onClick={(e) => e.stopPropagation()}>
                            {lbt.matched_by === 'manual' && lbt.btp_id && (
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                disabled={busy}
                                onClick={() => unlink(lbt.btp_id!)}
                                title="Unlink (removes the link without touching the payment or bank transaction)"
                              >
                                Unlink
                              </Button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {linkedBTs.some(lbt => !lbt.deleted && lbt.matched_by === 'auto') && (
                <Alert variant="info" className="mt-2 mb-0 small">
                  <i className="bi bi-info-circle me-1"></i>
                  <strong>Auto-linked.</strong> If this is the wrong bank
                  transaction, the cleanest fix is to correct the memo at
                  the source so every system shows the right invoice, then
                  re-sync. If your bank doesn't allow editing the memo
                  (some don't), click the row above to open the bank
                  transaction and add a manual entry in the Remittance
                  panel with the correct invoice number instead. Either
                  way, the next sync re-runs the auto-linker and replaces
                  this link.
                </Alert>
              )}
              {isStranded && exposureCount > 0 && (
                <div className="mt-2">
                  <Badge bg="warning" text="dark">
                    {exposureCount} commission{exposureCount === 1 ? '' : 's'} reference these invoices
                  </Badge>
                </div>
              )}
            </div>
          )}

          {/* Unlinkable reason. */}
          {isUnlinkable && (
            <div className="border rounded p-3">
              <h6 className="mb-2">Unlinkable</h6>
              <p className="mb-2 text-muted">
                Marked as not-to-be-linked. Hidden from the default Unlinked filter.
              </p>
              <dl className="row mb-0">
                <dt className="col-sm-3">Reason</dt>
                <dd className="col-sm-9">{payment.unlinkable_reason || '—'}</dd>
              </dl>
            </div>
          )}

          {/* Bank-transaction picker — only when unlinked AND user has linking role. */}
          {showLinkPicker && (
            <div className="d-flex flex-column flex-grow-1" style={{ minHeight: '320px' }}>
              <h5 className="mb-2 pb-2 border-bottom d-flex align-items-center gap-2">
                <i className="bi bi-link-45deg"></i>
                <span>Link Bank Transaction(s)</span>
              </h5>
              <BankTransactionsTable
                candidateBankTransactions={candidates}
                selectedIds={selectedBtIds}
                onSelectionChange={setSelectedBtIds}
                onRowClick={(bt) => bt.id && openBankTxn(bt.id)}
                onRevalidate={handleRevalidateBT}
                showDateNav={true}
                title=""
                countLabel={(n) => n !== undefined ? `${n} match${n === 1 ? '' : 'es'}` : 'No matches'}
                preferencesKey="bank_transactions_link_modal"
                userId={user?.id}
                userPreferences={userData?.preferences}
              />
            </div>
          )}

          {/* Mark Unlinkable — only valid for unlinked payments. A payment
              that already has a BTP (linked or stranded) is past the point
              where Unlinkable makes sense; for stranded the path is
              Void / Delete. */}
          {isUnlinked && canLink && (
            <div className="border rounded p-3">
              <Form.Check
                type="switch"
                id="mark-unlinkable"
                label="This payment will never have a bank transaction (mark unlinkable)"
                checked={markUnlinkable}
                onChange={e => {
                  const on = e.target.checked;
                  setMarkUnlinkable(on);
                  // Picking "unlinkable" means we're deciding NOT to link
                  // to any bank transaction — clear the picker selection so
                  // the previously-checked rows don't visually persist or
                  // get committed if the user tabs back to Save.
                  if (on) setSelectedBtIds([]);
                }}
              />
              {markUnlinkable && (
                <Form.Group className="mt-2">
                  <Form.Label className="mb-1">Reason <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={unlinkableReason}
                    onChange={e => setUnlinkableReason(e.target.value)}
                    placeholder="e.g. Deposited to an account we don't sync; payment is from before our Mercury fetch window; ..."
                    required
                  />
                </Form.Group>
              )}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-between">
          <div className="d-flex gap-2">
            {canLink && (isLinked || isStranded || isUnlinked) && payment.status !== 'voided' && (
              <Button variant="outline-warning" onClick={voidPayment} disabled={busy}>Void Payment</Button>
            )}
            {canLink && (
              <Button variant="outline-danger" onClick={deletePayment} disabled={busy}>Delete Payment</Button>
            )}
            {canLink && isUnlinkable && (
              <Button variant="outline-secondary" onClick={markLinkable} disabled={busy}>Mark Linkable</Button>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={onClose} disabled={busy}>Close</Button>
            {markUnlinkable && isUnlinked && canLink && (
              <Button variant="warning" onClick={submitMarkUnlinkable} disabled={busy || !unlinkableReason.trim()}>
                {busy && <Spinner animation="border" size="sm" className="me-1" />}
                Mark Unlinkable
              </Button>
            )}
            {showLinkPicker && !markUnlinkable && (
              <Button variant="primary" onClick={saveLink} disabled={busy || selectedBtIds.length === 0}>
                {busy && <Spinner animation="border" size="sm" className="me-1" />}
                Save Link{selectedBtIds.length > 1 ? ` (${selectedBtIds.length})` : ''}
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>

      {invoiceModalId && (
        <InvoiceDetailModal
          show
          invoiceId={invoiceModalId}
          onHide={() => setInvoiceModalId(null)}
        />
      )}

      {selectedBankTxn && (
        <TransactionDetailModal
          key={selectedBankTxn.id}
          transaction={selectedBankTxn}
          onClose={() => setSelectedBankTxn(null)}
          onUpdated={refresh}
        />
      )}
    </>
  );
};

// PaymentAttachments fetches and renders attachments synced from QuickBooks.
// Hidden when there are none so we don't clutter the modal for payments with
// no attached files.
const PaymentAttachments: React.FC<{ paymentId: string }> = ({ paymentId }) => {
  const { data } = useQuery({
    queryKey: ['payment-attachments', paymentId],
    queryFn: () => getInvoicePaymentsPaymentIdAttachments(paymentId),
    enabled: !!paymentId,
  });

  const attachments: PaymentAttachment[] = (data as any)?.data || [];

  const downloadFn = useCallback(async (attachmentId: string): Promise<Blob> => {
    const rawBlob = await getInvoicePaymentsPaymentIdAttachmentsAttachmentId(paymentId, attachmentId);
    return rawBlob as unknown as Blob;
  }, [paymentId]);

  if (attachments.length === 0) return null;

  return (
    <div className="border rounded p-3">
      <h6 className="mb-3">Attachments</h6>
      <AttachmentViewer attachments={attachments} downloadFn={downloadFn} />
    </div>
  );
};

export default PaymentDetailModal;
